import { useMemo, useState } from 'react';

import { Loading } from '@/components/shared/Loading/Loading';
import { NoResults } from '@/components/shared/NoResults/NoResults';
import { useDebounce } from '@/lib/hooks/useDebounce';

import { useContentQueries } from '../../services/ContentManagerService';
import CollectionCard from './CollectionCard';
import CollectionFilters, { GetCollectionParams } from './CollectionFilters';

interface CollectionsListProps {
  onEdit: (slug: string) => void;
  onDelete: (slug: string) => void;
  onViewBlocks: (slug: string) => void;
}

const CollectionsList = ({ onEdit, onDelete, onViewBlocks }: CollectionsListProps) => {
  const { useCollections } = useContentQueries();

  const [filters, setFilters] = useState<GetCollectionParams>({
    title: '',
    language: undefined,
    status: [],
    pageSize: 20,
  });

  const debouncedTitle = useDebounce(filters.title, 300);

  const queryFilters = useMemo(
    () => ({
      ...filters,
      title: debouncedTitle,
    }),
    [filters, debouncedTitle],
  );

  const { data, isLoading, error } = useCollections(queryFilters);

  const collections = data?.collections || [];

  const handleFilterChange = (
    key: keyof GetCollectionParams,
    value: GetCollectionParams[keyof GetCollectionParams],
  ) => {
    setFilters((prev) => {
      const normalizedValue =
        key === 'status' && Array.isArray(value) && value.length === 0 ? undefined : value;
      return { ...prev, [key]: normalizedValue };
    });
  };

  const renderContent = useMemo(() => {
    if (isLoading) {
      return <Loading />;
    }

    if (error) {
      return (
        <div className="text-center py-10 text-red-600 bg-red-50 border border-red-200 rounded-lg">
          {error instanceof Error ? error.message : 'Failed to load collections'}
        </div>
      );
    }

    if (!collections.length) {
      return <NoResults />;
    }

    return (
      <div className="grid gap-3">
        {collections.map((c) => (
          <CollectionCard
            key={c.slug}
            collection={c}
            onEdit={onEdit}
            onDelete={onDelete}
            onViewBlocks={onViewBlocks}
          />
        ))}
      </div>
    );
  }, [collections, error, isLoading, onEdit, onDelete, onViewBlocks]);

  return (
    <div className="grid gap-4">
      <CollectionFilters filters={filters} onFilterChange={handleFilterChange} />
      {renderContent}
    </div>
  );
};

export default CollectionsList;
